import {
  useCallback, useEffect, useMemo, useState, useTransition,
} from 'react';
import {
  type AgsReportDataResponse, getAgsReportRingData,
} from '~/exportReport/exportReport.repository';
import {
  createAgsReportDataRingRequest,
  getReportDemographicDataFromResults,
  getReportDemographicLabelFromResults, type ReportDemographicData,
} from '~/exportReport/exportReportData.helpers';
import { ExportReportType } from '~/exportReport/exportReportType';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import { useLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';
import { useToolsStateExportReportSelector } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.selectors';
import type { ReportTableColumn } from './table/ReportTable.model';

type UseExportReportDataParams = {
  isPreviewMode?: boolean;
  template: ReportTemplate | null;
  tableColumns: ReadonlyArray<ReportTableColumn>;
};

export const useExportReportData = ({ isPreviewMode = false, template, tableColumns }: UseExportReportDataParams) => {
  const [data, setData] = useState<AgsReportDataResponse | null>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, startTransition] = useTransition();
  const exportReportState = useToolsStateExportReportSelector();

  const spreadsheetData = useLatLngSpreadsheetData();

  const center = useMemo(() => {
    if (!exportReportState.isActive || exportReportState.params.type !== ExportReportType.Location) {
      return null;
    }

    const rowData = spreadsheetData.getRow(exportReportState.params.data.markerId);

    return rowData ?? null;
  }, [exportReportState, spreadsheetData]);

  const getDemographicData = useCallback((columnId: string, demographicId: string): ReportDemographicData | null => {
    if (isPreviewMode) {
      return null;
    }

    return getReportDemographicDataFromResults(data, columnId, demographicId);
  }, [data, isPreviewMode]);

  const getDemographicLabel = useCallback((demographicId: string): string | null => {
    return getReportDemographicLabelFromResults(data, demographicId);
  }, [data]);

  useEffect(() => {
    setIsError(false);

    if (!template || !center || isPreviewMode) {
      return;
    }

    const reportDataRequest = createAgsReportDataRingRequest({
      center,
      tableColumns,
      varlistId: template.varlistId,
    });

    startTransition(async () => {
      try {
        if (reportDataRequest.radiiInMiles.length) {
          const results = await getAgsReportRingData(reportDataRequest);

          setData(results.report);
        }
      }
      catch {
        setIsError(true);
      }
    });
  }, [center, isPreviewMode, template, tableColumns]);

  useEffect(() => {
    if (!data) {
      return;
    }

    Object.assign(window, {
      isReportFullyLoaded: true,
    });
  }, [data]);

  return {
    isLoading,
    isError,
    getDemographicData,
    getDemographicLabel,
  };
};
